import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/config/site";
import { scrollToId } from "@/lib/scroll";
import { useDownload } from "@/hooks/useDownload";
import { Logo } from "./Logo";
import { EASE } from "./Motion";

const useScrollState = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    NAV_LINKS.forEach(({ id }) => document.getElementById(id) && io.observe(document.getElementById(id)));
    return () => { window.removeEventListener("scroll", onScroll); io.disconnect(); };
  }, []);
  return { scrolled, active };
};

const MobileMenu = ({ onNav, onClose }) => (
  <motion.div
    data-testid="mobile-menu"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.6, ease: EASE }}
    className="fixed inset-0 z-40 flex flex-col bg-pa-bg px-5 pb-10 pt-28 lg:hidden"
  >
    <nav aria-label="Mobile" className="flex flex-col">
      {NAV_LINKS.map((l, i) => (
        <motion.button
          key={l.id}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 + i * 0.06, duration: 0.6, ease: EASE }}
          onClick={() => onNav(l.id)}
          data-testid={`mobile-nav-${l.id}`}
          className="flex items-baseline justify-between border-b border-white/10 py-4 text-left font-display text-5xl uppercase text-pa-ink"
        >
          {l.label}
          <span className="font-mono text-xs text-pa-dim">0{i + 1}</span>
        </motion.button>
      ))}
    </nav>
    <button onClick={onClose} className="sr-only">Close menu</button>
  </motion.div>
);

export const Navbar = () => {
  const { scrolled, active } = useScrollState();
  const [open, setOpen] = useState(false);
  const download = useDownload();

  const nav = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
          scrolled || open ? "border-b border-white/10 bg-pa-bg/85 backdrop-blur-xl" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-6 px-5 sm:px-8 lg:h-[72px] lg:px-12">
          <button onClick={() => nav("top")} aria-label="Pitchally — back to top" className="shrink-0">
            <Logo testId="navbar-logo" />
          </button>
          <nav aria-label="Primary" className="hidden items-center gap-9 lg:flex">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => nav(l.id)}
                data-testid={`nav-link-${l.id}`}
                className={`relative text-[13px] font-medium uppercase tracking-[0.16em] transition-colors duration-300 ${
                  active === l.id ? "text-pa-ink" : "text-pa-muted hover:text-pa-ink"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-px bg-pa-orange glow-line transition-[width] duration-500 ease-out-expo ${active === l.id ? "w-full" : "w-0"}`}
                />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => download({ source: "navbar" })}
              data-testid="navbar-download-btn"
              className="h-10 bg-pa-orange px-4 text-[12px] font-semibold uppercase tracking-[0.16em] text-pa-black shadow-[3px_3px_0_0_#F5F1EB] transition-[transform,box-shadow] duration-300 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0_0_#F5F1EB,0_0_32px_rgba(242,101,49,0.6)] sm:px-5"
            >
              Download app
            </button>
            <button
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
              className="grid h-10 w-10 place-items-center border border-white/15 text-pa-ink lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onNav={nav} onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
